import { z } from 'zod';

/**
 * ATC 定义版本的对外契约（C-096，方案见 C-074 四）。
 *
 * **只有 POST，没有 PATCH。** 给了 PATCH，前端就会长出「编辑四段」的心智，
 * 而库层会拒绝——两边语义打架，最后总是有人去绕库层。
 * 定义是仅追加的（I-3），提一版新的到集合上，HTTP 语义与库层语义因此一致。
 */

/**
 * 提交一版新定义。
 *
 * **`.strict()` 必须写在这里，而且要先于任何「多余字段会被拒」的说明。**
 * zod 默认行为是**静默剥掉**未声明的字段——没有 `.strict()` 时，
 * 调用方传 `contentHash` 会被丢掉而请求照样 200，
 * 于是「传了也不认」这句话是**假的**：它确实没生效，但调用方以为生效了。
 * 昨天就是这么写反过一次（先写注释后写实现，注释描述的是意图不是行为）。
 *
 * 三个字段**刻意不在这里出现**，因为它们由库决定，不接受调用方传入：
 * - `contentHash`   —— 由 `atc_version_seal_trg` 覆盖式赋值
 * - `versionNo`     —— 由 `atc_add_version()` 取号（锁 atc 主行串行）
 * - `currentVersionId` —— 由 `atc_add_version()` 移动指针
 *
 * 不设字段 + `.strict()` 双保险：前者让它无从传入，后者让传了当场 400，
 * 而不是被悄悄忽略。**被忽略是最坏的**——调用方以为设了，实际没设。
 */
export const createAtcVersionSchema = z
  .object({
    /** WHAT：这张卡产出什么。 */
    what: z.string(),
    /** HOW：判断规则与铁律，不是步骤。 */
    how: z.string(),
    /** ACTION：动作序列与输入输出。 */
    actionSpec: z.record(z.string(), z.unknown()),
    /** TEST：完成判据。 */
    testSpec: z.record(z.string(), z.unknown()),
    triggerType: z.enum(['schedule', 'event', 'manual', 'undetermined']),
    triggerSpec: z.record(z.string(), z.unknown()).default({}),
    hotlGate: z.boolean(),
    determinism: z.enum(['deterministic', 'exploratory']),
    /**
     * 这一版方法是谁做的：human=人（V0）/ agent=AI（V1）。
     * **必填**——「靠 action_package_ref 空不空推断」已在 C-185 裁定为不可接受：
     * 隐式表达出错时无处报（与 ADR-0030 八同形）。
     */
    authoredBy: z.enum(['human', 'agent']),
  })
  .strict();

export const atcVersionSchema = z.object({
  atcVersionId: z.string(),
  versionNo: z.number(),
  what: z.string(),
  how: z.string(),
  actionSpec: z.unknown(),
  testSpec: z.unknown(),
  triggerType: z.string(),
  triggerSpec: z.unknown(),
  hotlGate: z.boolean(),
  determinism: z.string(),
  contentHash: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
  /** 是否为该卡当前生效的版本。 */
  isCurrent: z.boolean(),

  /* ═══════ 本质模型 v1.0 落库的五列（迁移 20260821151000），卡页要据实呈现 ═══════ */

  /** 这一版方法是谁做的：human=人 / agent=AI。显式二值，非空。 */
  authoredBy: z.enum(['human', 'agent']),
  /** true = 回填时按 action_package_ref 空不空推出来的，不是记录者给的。推断值必须可识别。 */
  authoredByInferred: z.boolean(),
  /** 方法论保护级别：这一定义的哪一段不给谁看。null = 未设定。 */
  protectionLevel: z.string().nullable(),
  /** 执行主体登记（结构化）：哪一段人工、哪一段智能体。null = 未登记。 */
  executorSpec: z.unknown().nullable(),
  /** ACTION 包引用。包本体无表（刻意不建），存量全 NULL——卡页照实显「未登记」。 */
  actionPackageRef: z.string().nullable(),
});

export const atcVersionListSchema = z.object({
  code: z.string(),
  versions: z.array(atcVersionSchema),
});

export const createAtcVersionResultSchema = z.object({
  atcVersionId: z.string(),
  versionNo: z.number(),
  contentHash: z.string(),
  /**
   * 新版内容与上一版**逐字相同**。
   *
   * 不因此拒绝提交——「内容没变但换了人确认」也是一次有意义的事件，
   * 拒绝会让审批留痕断掉。但要让调用方看得见，否则一次误点会静默多出一版。
   */
  unchangedFromPrevious: z.boolean(),
});

export type CreateAtcVersion = z.infer<typeof createAtcVersionSchema>;
export type AtcVersion = z.infer<typeof atcVersionSchema>;
export type AtcVersionList = z.infer<typeof atcVersionListSchema>;
export type CreateAtcVersionResult = z.infer<typeof createAtcVersionResultSchema>;

/* ═══════ 标准登记（standard_ref + atc_standard，迁移 20260821151000） ═══════
 *
 * 「这张卡按什么标准做」——行业/企业标准。挂**版本**不挂本体：
 * 换标准后历史执行按当时那版的标准受审；挂本体则历史被改写成今天的标准。
 */

/** 一条标准的登记。锚 = 编号 + 版本。 */
export const standardRefSchema = z.object({
  standardRefId: z.string(),
  standardNo: z.string(),
  version: z.string().nullable(),
  /** 标准类型（行业 / 企业……），取值由登记方维护。 */
  kind: z.string().nullable(),
  title: z.string(),
  publisher: z.string().nullable(),
  effectiveFrom: z.string().nullable(),
  effectiveTo: z.string().nullable(),
});
export type StandardRef = z.infer<typeof standardRefSchema>;

/** 一张卡**当前版本**登记的标准清单。 */
export const atcStandardListSchema = z.object({
  code: z.string(),
  rows: z.array(standardRefSchema),
});
export type AtcStandardList = z.infer<typeof atcStandardListSchema>;
